import { useEffect, useRef } from 'react';
import { AutoSizer, Collection, WindowScroller } from 'react-virtualized';

import useIsFirstRender from '@/hooks/useIsFirstRender';
import useProtectActionUnregistered from '@/hooks/useProtectActionUnregistered';
import useScreenSize from '@/hooks/useScreenSize';
import useStateHandler from '@/hooks/useStateHandler';
import ArtworkCard from '@/shared-components/cards/components/ArtworkCard';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';
import UnregisteredPopup from "@/shared-components/popups/unregistered-user-popup";

import "./styles/VirtualList.css";

export default function VirtualList({ items, onLoadMore, navigateToArtwork }) {
  const screenSize = useScreenSize();
  const columnSizesCache = useRef(initColumnSizesCache());
  const sizeAndPositionCache = useRef([]);
  const collectionRef = useRef();
  const isFirstRender = useIsFirstRender();
  const { state } = useStateHandler();
  const { privateActionHandlerUnregistered, showPopup } = useProtectActionUnregistered({ typeOfProtection: "USER_LIMIT_VIEW" });

  function handleClick(event) {
    privateActionHandlerUnregistered(event);
    return event?.defaultPrevented;
  }

  function initColumnSizesCache() {
    const columnCount = screenSize.width <= 700 ? 2 : 3;
    const spacer = screenSize.width <= 550 ? 10 : 15;
    const widthOfList = Math.max((() => {
      if (screenSize.width >= 1230) return screenSize.width - 120;
      if (screenSize.width > 1000) return screenSize.width - 80;
      return screenSize.width - 22;
    })(), 0);
    const columnWidth = (widthOfList - spacer*(columnCount-1)) / columnCount;
    let columnStarts = [];
    let columnHeights = [];

    for(let i=0; i<columnCount; i++) {
      if(i===0) columnStarts[i] = 0; 
      else columnStarts[i] = columnStarts[i-1] + columnWidth + spacer;
      columnHeights[i] = 0;
    }

    return { columnCount, columnWidth, spacer, columnStarts, columnHeights };
  }

  function resolveProfilePreviewImageSize(columnWidth, aspectRatio) {
    const safeWidth = Number.isFinite(columnWidth) && columnWidth > 0 ? columnWidth : 300;
    const safeAspectRatio = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1;
    const smallerDimension = safeAspectRatio > 1 ? safeWidth / safeAspectRatio : safeWidth;
    const devicePixelRatio = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    const cappedPixelRatio = Math.min(devicePixelRatio, 2);
    return Math.min(Math.ceil(smallerDimension * cappedPixelRatio), 2000);
  }

  function cellSizeAndPositionGetter({ index }) {
    if(!sizeAndPositionCache.current[index]) {
      const heightOfImage = columnSizesCache.current.columnWidth / items.data[index].artwork_media.artwork_main_picture.image_original_data.aspect_ratio;
      const heightOfItem = screenSize.width <= 550 ? heightOfImage + 72.5 : screenSize.width <= 1004 ? heightOfImage + 75.5 : heightOfImage + 82;
      const shortestColumn = columnSizesCache.current.columnHeights.indexOf(Math.min(...columnSizesCache.current.columnHeights));
      const item_x = columnSizesCache.current.columnStarts[shortestColumn];
      const item_y = columnSizesCache.current.columnHeights[shortestColumn];
    
      columnSizesCache.current.columnHeights[shortestColumn] += heightOfItem + columnSizesCache.current.spacer;

      const calculatedSizeAndPosition = {
        width: columnSizesCache.current.columnWidth,
        height: heightOfItem,
        x: item_x,
        y: item_y
      };

      sizeAndPositionCache.current[index] = calculatedSizeAndPosition;

      return calculatedSizeAndPosition;
    } else {
      return sizeAndPositionCache.current[index];
    }
  }

  useEffect(() => {
    if(!isFirstRender) {
      columnSizesCache.current = initColumnSizesCache();
      sizeAndPositionCache.current = [];
      collectionRef.current.recomputeCellSizesAndPositions();
    } 
  }, [screenSize]);

  return (
    <>
      {showPopup && <UnregisteredPopup showPopUp={showPopup}/>}
      <InfiniteScroll
        onLoadMore={onLoadMore}
        shouldLoadMore={items.data.length < items.totalDocs}
        listLength={items.data.length}
        className="user-profile-artworks__infinite-scroll-wrapper"
        defaultInfiniteLoaderClassName="user-profile-artworks__infinite-scroll-spinner"
      >
        {({ infiniteScrollAssigner }) => (
          <WindowScroller>
            {({ height, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => (
                  <Collection
                    autoHeight
                    width={width === 0 ? 0.1 : width}
                    height={height}
                    scrollTop={scrollTop}
                    ref={collectionRef}
                    cellCount={items.data.length}
                    cellSizeAndPositionGetter={cellSizeAndPositionGetter}
                    className="remove-scrollbar"
                    cellRenderer={({ index, key, style }) => (
                      <div
                        key={key}
                        style={style}
                        ref={infiniteScrollAssigner(index)}
                      >
                        {(() => {
                          const aspectRatio =
                            items.data[index]?.artwork_media?.artwork_main_picture?.image_original_data?.aspect_ratio;
                          const previewImageSize = resolveProfilePreviewImageSize(
                            columnSizesCache.current.columnWidth,
                            aspectRatio
                          );

                          return (
                            <ArtworkCard
                              artworkData={items.data[index]}
                              previewImageSize={previewImageSize}
                              onClick={(event) => {
                                if (!state.user_session && handleClick(event)) {
                                  return;
                                }
                                navigateToArtwork(items.data[index].slug);
                              }}
                            />
                          );
                        })()}
                      </div>
                    )}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteScroll>
    </>
  );
}
